import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  add,
  addDays,
  addHours,
  isBefore,
  isValid,
  parse,
  set,
} from "date-fns";
import {
  CalendarIcon,
  CopyIcon,
  LightbulbIcon,
  PlusIcon,
  SaveIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import uuid from "uuid";

import { formatDate } from "simplydo/core/dates";
import { setLocalStorageItem } from "simplydo/core/helpers";
import { RichText } from "simplydo/react/richtext";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { MSTeamsIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
  CheckboxWhenChecked,
} from "simplydo/react/ui/checkbox";
import { DateInput, DateTimeInput, TimeInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import { Message } from "simplydo/react/ui/message";
import { Table, TableCell, TableRow } from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useAppSelector } from "store";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import UserChip from "components/lib/Chips/UserChip";
import type { ChipUser } from "components/lib/Chips/types";
import { FileChooserWithDropzone, UserChooser } from "components/lib/Choosers";
import type { ChoosableUser as ChooserUser } from "components/lib/Choosers/types";
import UserConnectionManager from "components/lib/UserConnectionManager";

import TimezoneExplainer from "../TimezoneExplainer";
import type { AnnouncementSetState, EditableAnnouncement } from "./types";

type InputChangeHandler = (_e: FormEvent, data: { value: string }) => void;

type CheckboxChangeHandler = (
  _e: FormEvent,
  data: { checked?: boolean },
) => void;

type TextAreaChangeHandler = (
  _e: FormEvent,
  data: { value?: string | number },
) => void;

const formatTeamsMeetingTemplate = (teamsMeeting: {
  name: string;
  startDate: string;
  endDate: string;
  link: string;
}) => {
  return `<p></p><p>Join <b>${teamsMeeting.name}</b> in Microsoft Teams (${formatDate(teamsMeeting.startDate)} - ${formatDate(teamsMeeting.endDate)}): <a href="${teamsMeeting.link}">${teamsMeeting.link}</a></p>`;
};

const defaultSendAt = set(addDays(new Date(), 1), {
  hours: 9,
  minutes: 0,
  seconds: 0,
  milliseconds: 0,
}).toISOString();

type AnnouncementBaseProps = {
  forType: string;
  forId: string;
  announcement: EditableAnnouncement;
  setAnnouncement: AnnouncementSetState;
};

const AnnouncementBase = ({
  forType,
  forId,
  announcement,
  setAnnouncement,
}: AnnouncementBaseProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [renamingAttachmentId, setRenamingAttachmentId] = useState("");
  const [renamingAttachmentName, setRenamingAttachmentName] = useState("");
  const [activeAccordionIndexes, setActiveAccordionIndexes] = useState<
    number[]
  >([]);
  const [creatingTeamsMeeting, setCreatingTeamsMeeting] = useState(false);
  const [teamsMeetingName, setTeamsMeetingName] = useState(
    announcement.teamsMeeting?.subject || "",
  );
  const [teamsMeetingDescription, setTeamsMeetingDescription] = useState(
    announcement.teamsMeeting?.description || "",
  );
  const [teamsMeetingStartDate, setTeamsMeetingStartDate] = useState(
    announcement.teamsMeeting?.startDate
      ? new Date(announcement.teamsMeeting.startDate).toISOString()
      : new Date().toISOString(),
  );
  const [teamsMeetingEndDate, setTeamsMeetingEndDate] = useState(
    announcement.teamsMeeting?.endDate
      ? new Date(announcement.teamsMeeting.endDate).toISOString()
      : add(new Date(), { hours: 1 }).toISOString(),
  );
  const [teamsMeetingLink, setTeamsMeetingLink] = useState(
    announcement.teamsMeeting?.joinWebUrl || "",
  );

  const userIsAdmin = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id,
  );

  const toggleActiveAccordion = useCallback((index: number) => {
    setActiveAccordionIndexes((prevState) => {
      if (prevState.includes(index)) {
        return prevState.filter((i) => i !== index);
      } else {
        return [...prevState, index];
      }
    });
  }, []);

  const { calendarEvent } = announcement;

  useEffect(() => {
    if (announcement?._id) return;
    setLocalStorageItem(`announcement-${forId}`, JSON.stringify(announcement));
  }, [announcement, forId]);

  const addAttachments = useCallback(
    (files: { savedName: string; fileName: string }[]) => {
      toast.success("Upload successful");
      setAnnouncement((prevState) => ({
        ...prevState,
        attachments: [
          ...(prevState.attachments ?? []),
          ...files.map((file) => ({
            value: file.savedName,
            name: file.fileName,
            _id: uuid.v4(),
          })),
        ],
      }));
    },
    [setAnnouncement],
  );

  const onChange = useCallback(
    (value: string, text: string) => {
      setAnnouncement((prevState) => ({ ...prevState, body: value, text }));
    },
    [setAnnouncement],
  );

  const createTeamsMeeting = useCallback(() => {
    if (!teamsMeetingName) {
      toast.error("Meeting name is required.");
      return;
    }
    if (
      !isValid(new Date(teamsMeetingStartDate)) ||
      !isValid(new Date(teamsMeetingEndDate))
    ) {
      toast.error("Please select valid start and end dates.");
      return;
    }
    if (isBefore(teamsMeetingEndDate, teamsMeetingStartDate)) {
      toast.error("Meeting end date must be after the start date.");
      return;
    }

    setCreatingTeamsMeeting(true);
    api.announcements.createTeamsMeeting(
      forType,
      forId,
      {
        name: teamsMeetingName,
        description: teamsMeetingDescription,
        startDate: teamsMeetingStartDate,
        endDate: teamsMeetingEndDate,
      },
      ({ meeting }) => {
        const joinWebUrl = meeting?.joinWebUrl;
        if (!joinWebUrl) {
          toast.error("Meeting created but no join URL was returned.");
          setCreatingTeamsMeeting(false);
          return;
        }

        setTeamsMeetingLink(joinWebUrl);
        setAnnouncement((prevState) => {
          return {
            ...prevState,
            body: `${prevState.body || ""}${formatTeamsMeetingTemplate({
              name: meeting.subject!,
              startDate: teamsMeetingStartDate,
              endDate: teamsMeetingEndDate,
              link: joinWebUrl,
            })}`,
            teamsMeeting: meeting,
          };
        });
        toast.success("Teams meeting created.");
        setCreatingTeamsMeeting(false);
      },
      (err) => {
        toast.error(err.message);
        setCreatingTeamsMeeting(false);
      },
    );
  }, [
    forType,
    forId,
    teamsMeetingName,
    teamsMeetingDescription,
    teamsMeetingStartDate,
    teamsMeetingEndDate,
    setAnnouncement,
  ]);

  const customRecipientSelector = useMemo(() => {
    if (userIsAdmin) {
      return (
        <>
          <Divider hidden />
          <H5>
            To user
            <Subheader>
              When your announcement is triggered the announcement will be sent
              to the user that triggered the announcement. For example, if a an
              announcement is sent on submission of{" "}
              {t("generic.ideaWithArticle")}, the user that submitted the{" "}
              {t("generic.idea")} will be contacted. If you want to change this,
              please search for recipients below.
            </Subheader>
          </H5>
          <Dropdown
            variant="outline"
            value={announcement?.toUserCondition ?? "userThatTriggered"}
            onChange={(_e, { value }) => {
              setAnnouncement((prevState) => ({
                ...prevState,
                toUserCondition: String(value) as
                  | "userThatTriggered"
                  | "ideaOwner"
                  | "customUsers",
              }));
            }}
            options={[
              {
                key: "userThatTriggered",
                value: "userThatTriggered",
                text: "User that triggered the condition",
              },
              ...(forType === "challenge"
                ? [
                    {
                      key: "ideaOwner",
                      value: "ideaOwner",
                      text: `${t("common:capitalise", { key: "generic.idea" })} authors`,
                    },
                  ]
                : []),
              {
                key: "customUsers",
                value: "customUsers",
                text: "Select custom users",
              },
            ]}
          />
          {announcement?.toUserCondition === "customUsers" && (
            <>
              <UserChooser
                selectedUsers={
                  (announcement.ownerToUsers || []) as ChooserUser[]
                }
                trigger={
                  <Button style={{ marginTop: 5, marginLeft: 10 }}>
                    <Icon icon={PlusIcon} />
                    Add custom recipient
                  </Button>
                }
                enabledFeatures={{ search: true }}
                searchFunction={(query, success, failure) => {
                  if (!query) {
                    success([]);
                    return;
                  }
                  api.search.omni(
                    { query, include: ["users"] },
                    ({ users }) => users && success(users),
                    failure,
                  );
                }}
                onComplete={(users) => {
                  if (users.length === 0) {
                    return;
                  }
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    ownerToUsers: users as EditableAnnouncement["ownerToUsers"],
                    toUsers: users.map((u) => u._id),
                  }));
                }}
              />
              {announcement?.ownerToUsers?.length !== 0 ? (
                <div>
                  {announcement?.ownerToUsers?.map((recipient) => (
                    <div
                      key={recipient._id}
                      style={{
                        display: "flex",
                        marginTop: 20,
                        justifyContent: "space-between",
                        alignItems: "center",
                        maxWidth: "fit-content",
                      }}
                    >
                      <UserChip target="_blank" user={recipient as ChipUser} />
                      <Button
                        size="small"
                        onClick={() =>
                          setAnnouncement((prevState) => ({
                            ...prevState,
                            toUsers: (prevState.toUsers ?? []).filter(
                              (id) => id !== recipient._id,
                            ),
                            ownerToUsers: (prevState.ownerToUsers ?? []).filter(
                              (u) => u._id !== recipient._id,
                            ),
                          }))
                        }
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </>
      );
    }
    return null;
  }, [userIsAdmin, announcement, setAnnouncement, t, forType]);

  const fullScreenSelector = useMemo(() => {
    return (
      <>
        <Divider hidden />
        <H5>
          Full-screen announcement
          <Subheader>
            If enabled, {util.appName()} will also show this announcement in
            &quot;full-screen mode&quot; for the recipient, increasing the
            chance that they read it at the point of delivery.
          </Subheader>
        </H5>
        <Checkbox
          toggle
          checked={announcement.fullScreen}
          onChange={(_e, { checked }) =>
            setAnnouncement((prevState) => ({
              ...prevState,
              fullScreen: checked,
            }))
          }
        >
          Enable full screen announcement
        </Checkbox>
      </>
    );
  }, [announcement, setAnnouncement]);

  const updateSendAt = useCallback(
    (type: "date" | "time", value: string) => {
      const currentSendAt = announcement.sendAt ?? "";
      const date =
        type === "date"
          ? value
          : currentSendAt
            ? formatDate(currentSendAt, "yyyy-MM-dd")
            : value;
      const time =
        type === "time"
          ? value
          : currentSendAt
            ? formatDate(currentSendAt, "HH:mm")
            : value;
      const newDate = parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date());
      setAnnouncement((prev) => ({
        ...prev,
        sendAt: isValid(newDate) ? newDate.toISOString() : value,
      }));
    },
    [announcement.sendAt, setAnnouncement],
  );

  const sendAtValid = useMemo(() => {
    if (!announcement.sendAt) return false;
    return isValid(new Date(announcement.sendAt));
  }, [announcement.sendAt]);
  const scheduledDate = useMemo(
    () =>
      announcement.sendAt ? formatDate(announcement.sendAt, "yyyy-MM-dd") : "",
    [announcement.sendAt],
  );
  const scheduledTime = useMemo(
    () => (announcement.sendAt ? formatDate(announcement.sendAt, "HH:mm") : ""),
    [announcement.sendAt],
  );

  return (
    <>
      <Card fluid>
        <CardContent>
          <H3>Content</H3>
          Customise the content of the announcement to be sent to users.
          <Message color="blue" size="small">
            <H4>
              <Icon icon={LightbulbIcon} />
              {t("announcements.form.placeholders.title")}
            </H4>
            <p>{t("announcements.form.placeholders.info")}</p>
            <List variant="bulleted" compact>
              <ListItem>
                <code>
                  <mark>{"{firstName}"}</mark>
                </code>{" "}
                {t("announcements.form.placeholders.firstName")}
              </ListItem>
              <ListItem>
                <code>
                  <mark>{"{lastName}"}</mark>
                </code>{" "}
                {t("announcements.form.placeholders.lastName")}
              </ListItem>
            </List>
          </Message>
          <Divider hidden />
          <FormField required>
            <FormLabel>{t("announcements.form.subject")}</FormLabel>
            <Input
              placeholder={`${t("announcements.form.subject")}...`}
              fluid
              value={announcement.subject}
              onChange={
                ((_e, { value }) =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    subject: value,
                  }))) as InputChangeHandler
              }
            />
          </FormField>
          <Divider hidden />
          <FormField required style={{ margin: 0 }}>
            <FormLabel>{t("announcements.form.body")}</FormLabel>
          </FormField>
          <RichText
            preset="simple"
            fullMentionLinks
            placeholder={t("announcements.form.message")}
            value={announcement.body}
            onChange={onChange}
          />
        </CardContent>
      </Card>
      <Accordion styled fluid>
        <AccordionTitle
          index={0}
          active={activeAccordionIndexes.includes(0)}
          onClick={() => toggleActiveAccordion(0)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <H3 style={{ marginBottom: 0 }}>Settings</H3>
            {announcement?.replyTo && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
              >{`Reply-to: ${announcement?.replyTo}`}</Label>
            )}
            {announcement?.callToActionText && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
              >{`Call-to action: ${announcement.callToActionText}`}</Label>
            )}
            {announcement?.toUsers && (
              <Label style={{ marginLeft: 10 }} color="blue" size="small">
                {announcement?.toUsers?.length === 1
                  ? `To user: ${announcement?.ownerToUsers?.[0]?.profile?.fullName}`
                  : `To users: ${announcement?.ownerToUsers?.[0]?.profile?.fullName} and ${util.pluralise((announcement?.ownerToUsers?.length ?? 1) - 1, "other", "others", true)}`}
              </Label>
            )}
          </div>
        </AccordionTitle>
        <AccordionContent active={activeAccordionIndexes.includes(0)}>
          <H5>
            Reply-to address
            <Subheader>
              A reply-to address is the designated email where responses to your
              email announcement will be sent.
            </Subheader>
          </H5>
          <FormField>
            <Input
              placeholder={t("placeholder.email")}
              value={announcement.replyTo}
              onChange={
                ((_e, { value }) =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    replyTo: value,
                  }))) as InputChangeHandler
              }
            />
          </FormField>
          <Divider hidden />
          <H5>
            Call-to-action
            <Subheader>
              A call to action is a customizable button displayed at the end of
              your email that directs users to your desired destination.
            </Subheader>
          </H5>
          <Grid columns={3} mobile={1}>
            <GridColumn>
              <FormField>
                <FormLabel>Text</FormLabel>
                <Input
                  fluid
                  placeholder={`View on ${util.appName()}`}
                  value={announcement.callToActionText}
                  onChange={
                    ((_e, { value }) =>
                      setAnnouncement((prevState) => ({
                        ...prevState,
                        callToActionText: value,
                      }))) as InputChangeHandler
                  }
                />
              </FormField>
            </GridColumn>
            <GridColumn>
              <FormField>
                <FormLabel>URL</FormLabel>
                <Input
                  fluid
                  placeholder={`${sdiClickDomain}/linkTo${t("common:capitalise", { key: "generic.challenge" })}`}
                  value={announcement.callToActionLink}
                  onChange={
                    ((_e, { value }) =>
                      setAnnouncement((prevState) => ({
                        ...prevState,
                        callToActionLink: value,
                      }))) as InputChangeHandler
                  }
                />
              </FormField>
            </GridColumn>
            <GridColumn>
              <FormField>
                <FormLabel>Preview</FormLabel>
                <Button
                  style={{ fontWeight: 500, minWidth: 170 }}
                  color="primary"
                >
                  {announcement.callToActionText || `View on ${util.appName()}`}
                </Button>
              </FormField>
            </GridColumn>
          </Grid>
          {announcement?.type === "automatic" && customRecipientSelector}
          {announcement?.type === "automatic" && fullScreenSelector}
        </AccordionContent>
        <AccordionTitle
          index={1}
          active={activeAccordionIndexes.includes(1)}
          onClick={() => toggleActiveAccordion(1)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <H3 style={{ marginBottom: 0 }}>Attachments</H3>
            {(announcement.attachments?.length ?? 0) > 0 && (
              <Label
                style={{ marginLeft: 10 }}
                color="blue"
                size="small"
              >{`${util.pluralise(announcement.attachments?.length ?? 0, "attachment", "attachments", true)}`}</Label>
            )}
          </div>
        </AccordionTitle>
        <AccordionContent active={activeAccordionIndexes.includes(1)}>
          <p>
            Attach additional files to the announcement. Recipients will be able
            to download and access the attached files from the announcement.
          </p>
          {(announcement.attachments?.length ?? 0) > 0 ? (
            <Table>
              {announcement.attachments?.map((file) => (
                <TableRow key={file._id}>
                  <TableCell>
                    {renamingAttachmentId === file._id ? (
                      <Input
                        size="small"
                        fluid
                        defaultValue={renamingAttachmentName}
                        onChange={(_e, { value }) => {
                          setRenamingAttachmentName(value);
                        }}
                        action={
                          <Button
                            onClick={() => {
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                attachments: (prevState.attachments ?? []).map(
                                  (attachment) =>
                                    attachment._id === file._id
                                      ? {
                                          ...attachment,
                                          name: renamingAttachmentName,
                                        }
                                      : attachment,
                                ),
                              }));
                              setRenamingAttachmentId("");
                              setRenamingAttachmentName("");
                            }}
                          >
                            <Icon icon={SaveIcon} />
                            Done
                          </Button>
                        }
                      />
                    ) : (
                      file.name
                    )}
                  </TableCell>
                  <TableCell collapsing>
                    {!renamingAttachmentId ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setRenamingAttachmentId(file._id);
                          setRenamingAttachmentName(file.name);
                        }}
                        variant="outline"
                      >
                        <Icon icon={PencilIcon} />
                        Rename
                      </Button>
                    ) : null}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        util
                          .confirm(
                            "Remove attachment",
                            "Are you sure you want to remove this attachment?",
                          )
                          .then(() => {
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              attachments: (prevState.attachments ?? []).filter(
                                (attachment) => attachment._id !== file._id,
                              ),
                            }));
                          })
                          .catch(() => {});
                      }}
                      size="small"
                    >
                      <Icon icon={Trash2Icon} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          ) : null}
          <FileChooserWithDropzone
            maxMBFileSize={10}
            forType={forType}
            forId={forId}
            allowMultiple={true}
            onCompleteMultiple={addAttachments}
            onError={toast.error}
            popupProps={{ position: "bottom center", disabled: false }}
          />
        </AccordionContent>
        <AccordionTitle
          index={2}
          onClick={() => toggleActiveAccordion(2)}
          active={activeAccordionIndexes.includes(2)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <H3 style={{ marginBottom: 0 }}>Meeting</H3>
            {teamsMeetingName && (
              <Label style={{ marginLeft: 10 }} color="blue" size="small">
                {teamsMeetingName}
              </Label>
            )}
          </div>
        </AccordionTitle>
        <AccordionContent active={activeAccordionIndexes.includes(2)}>
          <p style={{ marginBottom: 10 }}>
            Create a Microsoft Teams meeting link, it will be displayed as a
            link in your announcement.
          </p>
          <UserConnectionManager
            entity="user"
            entityId={user?._id ?? ""}
            serviceProvider="microsoft"
            service="microsoftTeamsMeetings"
            contextId="microsoftTeamsMeetings"
            onConnect={(refreshCallback) => {
              toast.success("Microsoft Teams meetings connected");
              refreshCallback?.();
            }}
            onConnectionError={(errorMessage) => {
              toast.error(
                errorMessage || "Unable to connect Microsoft Teams meetings.",
              );
            }}
            renderDisconnected={({ connect, loading, isConnecting }) => (
              <Button
                type="button"
                size="small"
                disabled={loading || isConnecting}
                loading={loading || isConnecting}
                onClick={connect}
              >
                Connect Microsoft Teams
              </Button>
            )}
            renderConnected={() => (
              <>
                {teamsMeetingLink ? (
                  <Card fluid>
                    <CardContent>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 3,
                            }}
                          >
                            <Icon
                              icon={CalendarIcon}
                              size="large"
                              style={{ marginTop: 3 }}
                            />
                            <div>
                              <H4 style={{ margin: 0 }}>
                                {teamsMeetingName || "Teams meeting"}
                              </H4>
                              <p>
                                {formatDate(teamsMeetingStartDate)} -{" "}
                                {formatDate(teamsMeetingEndDate)}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div style={{ marginLeft: "auto" }}>
                          <Input
                            value={teamsMeetingLink}
                            readOnly
                            action={
                              <Button
                                onClick={() => {
                                  void util.copyToClipboard(teamsMeetingLink);
                                }}
                              >
                                <Icon icon={CopyIcon} />
                              </Button>
                            }
                            size="small"
                            style={{ marginRight: 45 }}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              util
                                .confirm(
                                  "Remove Microsoft Teams meeting",
                                  "Are you sure you want to delete this Microsoft Teams meeting?",
                                )
                                .then(() => {
                                  setTeamsMeetingLink("");
                                  setTeamsMeetingName("");
                                  setTeamsMeetingStartDate(
                                    new Date().toISOString(),
                                  );
                                  setTeamsMeetingEndDate(
                                    addHours(new Date(), 1).toISOString(),
                                  );
                                  setTeamsMeetingDescription("");
                                  setAnnouncement((prevState) => ({
                                    ...prevState,
                                    teamsMeeting: null,
                                    body: prevState.body.replace(
                                      formatTeamsMeetingTemplate({
                                        name: teamsMeetingName,
                                        startDate: teamsMeetingStartDate,
                                        endDate: teamsMeetingEndDate,
                                        link: teamsMeetingLink,
                                      }),
                                      "",
                                    ),
                                  }));
                                })
                                .catch(() => {});
                            }}
                            variant="outline"
                          >
                            <Icon icon={Trash2Icon} />
                          </Button>
                        </div>
                      </div>
                      {!calendarEvent ? (
                        <Message style={{ marginTop: 10 }}>
                          <div>
                            <p>
                              At the moment this announcement has no calendar
                              event attached. You can automatically create one
                              based on this meeting, this will enable recipients
                              to add the event as a reminder to their own
                              calendar.
                            </p>
                          </div>
                          <Button
                            size="small"
                            onClick={() => {
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                calendarEvent: {
                                  ...prevState.calendarEvent,
                                  startDate: teamsMeetingStartDate,
                                  endDate: teamsMeetingEndDate,
                                  organiserName: user?.profile?.fullName,
                                  organiserEmail: user?.emails[0]?.address,
                                  name: teamsMeetingName,
                                  location: "",
                                  description: teamsMeetingDescription,
                                },
                              }));
                            }}
                          >
                            Create calendar event
                          </Button>
                        </Message>
                      ) : null}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Grid columns={3} mobile={1} style={{ marginTop: 10 }}>
                      <GridColumn>
                        <FormField>
                          <FormLabel>Meeting name</FormLabel>
                          <Input
                            placeholder="Meeting name"
                            value={teamsMeetingName}
                            onChange={
                              ((_e, { value }) =>
                                setTeamsMeetingName(
                                  value,
                                )) as InputChangeHandler
                            }
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>Start date</FormLabel>
                          <DateTimeInput
                            placeholder="Start date"
                            name="teamsMeetingStartDate"
                            dateTimeFormat="yyyy-MM-dd HH:mm"
                            value={new Date(teamsMeetingStartDate)}
                            iconPosition="left"
                            onChange={
                              ((_e, { value }) => {
                                const nextStartDate = parse(
                                  value,
                                  "yyyy-MM-dd HH:mm",
                                  new Date(),
                                ).toISOString();
                                setTeamsMeetingStartDate(nextStartDate);
                                if (
                                  isBefore(teamsMeetingEndDate, nextStartDate)
                                ) {
                                  setTeamsMeetingEndDate(
                                    addHours(nextStartDate, 1).toISOString(),
                                  );
                                }
                              }) as InputChangeHandler
                            }
                          />
                        </FormField>
                      </GridColumn>
                      <GridColumn>
                        <FormField>
                          <FormLabel>End date</FormLabel>
                          <DateTimeInput
                            placeholder="End date"
                            name="teamsMeetingEndDate"
                            minDate={new Date(teamsMeetingStartDate)}
                            value={new Date(teamsMeetingEndDate)}
                            dateTimeFormat="yyyy-MM-dd HH:mm"
                            iconPosition="left"
                            onChange={
                              ((_e, { value }) =>
                                setTeamsMeetingEndDate(
                                  parse(
                                    value,
                                    "yyyy-MM-dd HH:mm",
                                    new Date(),
                                  ).toISOString(),
                                )) as InputChangeHandler
                            }
                          />
                        </FormField>
                      </GridColumn>
                    </Grid>
                    <FormField>
                      <FormLabel>Meeting description</FormLabel>
                      <TextArea
                        placeholder="(optional)"
                        value={teamsMeetingDescription}
                        onChange={
                          ((_e, { value }) =>
                            setTeamsMeetingDescription(
                              String(value || ""),
                            )) as TextAreaChangeHandler
                        }
                      />
                    </FormField>
                    <Button
                      type="button"
                      size="small"
                      loading={creatingTeamsMeeting}
                      disabled={!teamsMeetingName}
                      onClick={createTeamsMeeting}
                    >
                      <Icon icon={MSTeamsIcon} size="small" />
                      Create Teams meeting
                    </Button>
                  </>
                )}
              </>
            )}
          />
        </AccordionContent>
        <AccordionTitle
          index={3}
          active={activeAccordionIndexes.includes(3)}
          onClick={() => toggleActiveAccordion(3)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <H3 style={{ marginBottom: 0 }}>Calendar event</H3>
            {calendarEvent?.name && (
              <Label style={{ marginLeft: 10 }} color="blue" size="small">
                {calendarEvent?.name}
              </Label>
            )}
          </div>
        </AccordionTitle>
        <AccordionContent active={activeAccordionIndexes.includes(3)}>
          <p style={{ marginBottom: 10 }}>
            Create a calendar event that will be attached to emails sent with
            this announcement. This will enable recipients to add the event
            reminder to their own calendar.
          </p>

          <Button
            size="small"
            onClick={(e) => {
              if (calendarEvent) {
                setAnnouncement((prevState) => ({
                  ...prevState,
                  calendarEvent: null,
                }));
              } else {
                setAnnouncement((prevState) => ({
                  ...prevState,
                  calendarEvent: {
                    ...prevState.calendarEvent,
                    startDate: new Date().toISOString(),
                    endDate: addHours(new Date(), 1).toISOString(),
                    organiserName: user?.profile?.fullName,
                    organiserEmail: user?.emails[0]?.address,
                  },
                }));
              }
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {calendarEvent ? "Remove calendar event" : "Add calendar event"}
          </Button>
          {calendarEvent && (
            <>
              <Divider />
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <Grid columns={2} mobile={1}>
                  {calendarEvent.startDate ? (
                    <GridColumn>
                      <FormField>
                        <FormLabel>Start date</FormLabel>
                        <DateTimeInput
                          placeholder="Start date"
                          name="startDate"
                          dateTimeFormat="yyyy-MM-dd HH:mm"
                          value={new Date(calendarEvent.startDate)}
                          iconPosition="left"
                          onChange={
                            ((_e, { value }) => {
                              if (announcement?.calendarEvent?.endDate) {
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  calendarEvent: {
                                    ...prevState.calendarEvent,
                                    startDate: parse(
                                      value,
                                      "yyyy-MM-dd HH:mm",
                                      new Date(),
                                    ).toISOString(),
                                  },
                                }));
                              } else {
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  calendarEvent: {
                                    ...prevState.calendarEvent,
                                    startDate: parse(
                                      value,
                                      "yyyy-MM-dd HH:mm",
                                      new Date(),
                                    ).toISOString(),
                                    endDate: addHours(
                                      parse(
                                        value,
                                        "yyyy-MM-dd HH:mm",
                                        new Date(),
                                      ),
                                      1,
                                    ).toISOString(),
                                  },
                                }));
                              }
                            }) as InputChangeHandler
                          }
                        />
                      </FormField>
                    </GridColumn>
                  ) : null}
                  {calendarEvent.endDate ? (
                    <GridColumn>
                      <FormField>
                        <FormLabel>End date</FormLabel>
                        <DateTimeInput
                          placeholder="End date"
                          name="endDate"
                          minDate={
                            new Date(
                              calendarEvent.startDate ?? calendarEvent.endDate,
                            )
                          }
                          dateTimeFormat="yyyy-MM-dd HH:mm"
                          value={new Date(calendarEvent.endDate)}
                          iconPosition="left"
                          onChange={
                            ((_e, { value }) =>
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                calendarEvent: {
                                  ...prevState.calendarEvent,
                                  endDate: parse(
                                    value,
                                    "yyyy-MM-dd HH:mm",
                                    new Date(),
                                  ).toISOString(),
                                },
                              }))) as InputChangeHandler
                          }
                        />
                      </FormField>
                    </GridColumn>
                  ) : null}
                  <GridColumn>
                    <FormField
                      error={
                        !calendarEvent.organiserName
                          ? "Organiser name is required"
                          : false
                      }
                    >
                      <FormLabel>Organiser name</FormLabel>
                      <Input
                        placeholder="Organiser name"
                        value={calendarEvent.organiserName}
                        onChange={
                          ((_e, { value }) =>
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                organiserName: value,
                              },
                            }))) as InputChangeHandler
                        }
                      />
                    </FormField>
                  </GridColumn>
                  <GridColumn>
                    <FormField
                      error={
                        !calendarEvent.organiserEmail
                          ? "Organiser email is required"
                          : false
                      }
                    >
                      <FormLabel>Organiser email</FormLabel>
                      <Input
                        type="email"
                        placeholder="Organiser email"
                        onChange={
                          ((_e, { value }) =>
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                organiserEmail: value,
                              },
                            }))) as InputChangeHandler
                        }
                        value={calendarEvent.organiserEmail}
                      />
                    </FormField>
                  </GridColumn>
                </Grid>
                <Message>
                  The resulting calendar invitation will be displayed as
                  originating from this name and email address.
                </Message>
                <Grid columns={2} mobile={1}>
                  <GridColumn>
                    <FormField>
                      <FormLabel>Event name</FormLabel>
                      <Input
                        placeholder="Event name"
                        value={calendarEvent.name}
                        onChange={
                          ((_e, { value }) =>
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                name: value,
                              },
                            }))) as InputChangeHandler
                        }
                      />
                    </FormField>
                  </GridColumn>
                  <GridColumn>
                    <FormField>
                      <FormLabel>Location</FormLabel>
                      <Input
                        placeholder="(optional)"
                        onChange={
                          ((_e, { value }) =>
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                location: value,
                              },
                            }))) as InputChangeHandler
                        }
                        value={calendarEvent.location}
                      />
                    </FormField>
                  </GridColumn>
                  <GridColumn span={2}>
                    <FormField>
                      <FormLabel>Description</FormLabel>
                      <TextArea
                        placeholder="(optional)"
                        value={calendarEvent.description}
                        onChange={
                          ((_e, { value }) =>
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              calendarEvent: {
                                ...prevState.calendarEvent,
                                description: String(value ?? ""),
                              },
                            }))) as TextAreaChangeHandler
                        }
                      />
                    </FormField>
                  </GridColumn>
                </Grid>
              </div>
              <p>{`The calendar invite will be displayed as originating from your email address "${user?.emails[0]?.address}". Note that RSVPs may be received at this address.`}</p>
            </>
          )}
        </AccordionContent>

        {(announcement.type === "standard" ||
          announcement.type === "scheduled") && (
          <>
            <AccordionTitle
              index={4}
              onClick={() => toggleActiveAccordion(4)}
              active={activeAccordionIndexes.includes(4)}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <H3 style={{ marginBottom: 0 }}>Send when</H3>
                {announcement.sendAt && (
                  <Label style={{ marginLeft: 10 }} color="blue" size="small">
                    {formatDate(announcement.sendAt, "MMMM do yyyy 'at' HH:mm")}
                  </Label>
                )}
              </div>
            </AccordionTitle>
            <AccordionContent active={activeAccordionIndexes.includes(4)}>
              <p>
                This announcement can be scheduled to be sent at a later date
                instead of right away.
              </p>
              <Checkbox
                toggle
                checked={!!announcement.sendAt}
                onChange={
                  ((_e, { checked }) =>
                    setAnnouncement((prev) => ({
                      ...prev,
                      sendAt: checked ? prev.sendAt || defaultSendAt : null,
                    }))) as CheckboxChangeHandler
                }
                style={{ marginTop: "0.5rem" }}
              >
                <CheckboxHeader>
                  Schedule this announcement to be sent at a later date
                </CheckboxHeader>
                <CheckboxWhenChecked>
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <span>Send announcement on</span>
                      <DateInput
                        name="date"
                        placeholder="Date"
                        value={
                          sendAtValid ? scheduledDate : announcement.sendAt!
                        }
                        minDate={new Date()}
                        dateFormat="yyyy-MM-dd"
                        iconPosition="left"
                        onChange={(_e, { value }) =>
                          updateSendAt("date", value ?? "")
                        }
                        style={{ marginLeft: 10, marginRight: 10 }}
                      />
                      <span> at </span>
                      <TimeInput
                        name="time"
                        placeholder="Time"
                        value={
                          sendAtValid ? scheduledTime : announcement.sendAt!
                        }
                        minuteStep={15}
                        iconPosition="left"
                        onChange={(_e, { value }) =>
                          updateSendAt("time", value ?? "")
                        }
                        style={{ marginLeft: 10, marginRight: 10 }}
                      />
                    </div>
                    <TimezoneExplainer style={{ marginTop: 10 }} />
                  </>
                </CheckboxWhenChecked>
              </Checkbox>
            </AccordionContent>
          </>
        )}
      </Accordion>
    </>
  );
};

export default AnnouncementBase;
